import React, { useEffect, useRef, useState } from 'react';

import { Link as RouterLink, useLocation } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Alert, Box, Button, Chip, FormControlLabel, Link, Stack, Switch, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import AttributionCell from '~/components/GHADiscounting/AttributionCell';
import { formatTimestamp } from '~/components/GHADiscounting/utils';
import GHABulkOperationDialog from '~/components/GHAPropertyManagement/GHABulkOperationDialog';

import {
  useBulkUpsertGHABlockedIdentities,
  useDeleteGHABlockedIdentity,
  useGHABlockedIdentities,
  useUpsertGHABlockedIdentity,
} from '~/hooks/apis/connection-google/useGHABlockedIdentities';
import useAppNavigation from '~/hooks/useAppNavigation';
import useDebounced from '~/hooks/useDebounced';

import type { GHABlockedIdentity } from '~/services/ConnectionGoogleService';

import dayjs from '~/timeInit';

import BlockedIdentityDialog from './BlockedIdentityDialog';
import { GHA_BLOCKED_IDENTITY_EVIDENCE_FIELDS, GHA_EMAIL_DOMAIN_RULES_PAGE_SIZE_OPTIONS } from './constants';
import { parseBlockedIdentityFilterParams, parsePaginationParams, toExpiryDate } from './utils';

const getColumns = (
  onEdit: (identity: GHABlockedIdentity) => void,
  onDelete: (identity: GHABlockedIdentity) => void,
  isDeleting: boolean,
): Array<GridColDef<GHABlockedIdentity>> => [
  { field: 'value', headerName: 'Email', flex: 1, minWidth: 220 },
  {
    field: 'reason',
    headerName: 'Reason',
    flex: 1,
    minWidth: 180,
    renderCell: ({ row }) => (
      <Typography variant="body2" color="text.secondary">
        {row.reason ?? '—'}
      </Typography>
    ),
  },
  {
    field: 'evidence',
    headerName: 'Evidence',
    flex: 1,
    minWidth: 220,
    sortable: false,
    renderCell: ({ row }) => {
      const present = GHA_BLOCKED_IDENTITY_EVIDENCE_FIELDS.filter(({ key }) => row.evidence?.[key] != null);
      if (present.length === 0) {
        return (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        );
      }
      return (
        <Box>
          {present.map(({ key, label }) => (
            <Typography key={key} variant="caption" color="text.secondary" display="block">
              {label}:{' '}
              {key === 'propertyId' && row.evidence?.propertyId ? (
                <Link
                  component={RouterLink}
                  to={`/accommodation/properties/${encodeURIComponent(row.evidence.propertyId)}`}
                >
                  {row.evidence.propertyId}
                </Link>
              ) : (
                row.evidence?.[key]
              )}
            </Typography>
          ))}
        </Box>
      );
    },
  },
  {
    field: 'expiresAt',
    headerName: 'Expires',
    width: 170,
    renderCell: ({ row }) => {
      if (row.expiresAt == null) {
        return (
          <Typography variant="body2" color="text.secondary">
            Never
          </Typography>
        );
      }
      // Expired rows only show with the toggle on; they stay for the audit trail but never match.
      return new Date(row.expiresAt) <= new Date() ? (
        <Chip size="small" variant="outlined" label={`Expired ${toExpiryDate(row.expiresAt)}`} />
      ) : (
        <Typography variant="body2">{toExpiryDate(row.expiresAt)}</Typography>
      );
    },
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    type: 'dateTime',
    width: 200,
    valueGetter: (value) => dayjs(value).toDate(),
    renderCell: ({ row }) => <Typography variant="body2">{formatTimestamp(row.createdAt)}</Typography>,
  },
  {
    field: 'updatedAt',
    headerName: 'Created / updated',
    width: 260,
    renderCell: ({ row }) => (
      <AttributionCell
        createdBy={row.createdBy}
        updatedBy={row.updatedBy}
        createdAt={row.createdAt}
        updatedAt={row.updatedAt}
      />
    ),
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 170,
    sortable: false,
    align: 'right',
    headerAlign: 'right',
    renderCell: ({ row }) => (
      <>
        <Button size="small" onClick={() => onEdit(row)}>
          Edit
        </Button>
        <Button size="small" color="error" disabled={isDeleting} onClick={() => onDelete(row)}>
          Delete
        </Button>
      </>
    ),
  },
];

export default function BlockedIdentitiesPanel() {
  const location = useLocation();
  const { push, replace } = useAppNavigation();
  const searchParams = new URLSearchParams(location.search);
  const paginationModel = parsePaginationParams(searchParams);
  // Page and the expired toggle live in the URL like the domain-rules tab; the search text does not,
  // because it is often a whole address.
  const filters = parseBlockedIdentityFilterParams(searchParams);

  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [identityToEdit, setIdentityToEdit] = useState<GHABlockedIdentity | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const showConfirmDialog = useConfirmDialog();
  const debouncedSearch = useDebounced(search.trim(), 300);

  // A new search starts again from the first page. Replaced rather than pushed, so typing does not
  // leave a history entry per pause.
  const searchedFor = useRef(debouncedSearch);
  useEffect(() => {
    if (searchedFor.current === debouncedSearch) {
      return;
    }
    searchedFor.current = debouncedSearch;
    const params = new URLSearchParams(location.search);
    params.set('page', '0');
    replace({ search: params.toString() });
  }, [debouncedSearch, location.search, replace]);

  const { data, isFetching, error } = useGHABlockedIdentities({
    value: debouncedSearch === '' ? undefined : debouncedSearch,
    includeExpired: filters.includeExpired ? true : undefined,
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
  });
  const upsertMutation = useUpsertGHABlockedIdentity();
  const deleteMutation = useDeleteGHABlockedIdentity();
  const bulkMutation = useBulkUpsertGHABlockedIdentities();

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    const params = new URLSearchParams(location.search);
    params.set('page', String(model.page));
    params.set('pageSize', String(model.pageSize));
    push({ search: params.toString() });
  };

  const handleIncludeExpiredChange = (includeExpired: boolean) => {
    const params = new URLSearchParams(location.search);
    if (includeExpired) {
      params.set('includeExpired', 'true');
    } else {
      params.delete('includeExpired');
    }
    params.set('page', '0');
    push({ search: params.toString() });
  };

  const handleEdit = (identity: GHABlockedIdentity) => {
    setIdentityToEdit(identity);
    setDialogOpen(true);
  };

  const handleDelete = async (identity: GHABlockedIdentity) => {
    const confirmed = await showConfirmDialog({
      title: `Unblock ${identity.value}?`,
      description: 'This person is classified by the domain rules again, like any other shopper.',
    });
    if (confirmed) {
      deleteMutation.mutate(identity);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setIdentityToEdit(null);
    setSubmitError(null);
  };

  if (error) {
    return <Alert severity="error">Failed to load blocked identities</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            label="Search email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={filters.includeExpired}
                onChange={(_, checked) => handleIncludeExpiredChange(checked)}
              />
            }
            label="Show expired"
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="text" startIcon={<CloudUploadIcon />} onClick={() => setBulkOpen(true)}>
            Bulk upload
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Block identity
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Each row blocks one person's GHA discount at every property, whatever their email domain. An address within two
        characters of a blocked one is flagged as uncertain. Shadow mode: the result is logged on voucher mint and
        nothing acts on it yet.
      </Alert>

      <DataGrid
        rows={data?.result ?? []}
        columns={getColumns(handleEdit, handleDelete, deleteMutation.isPending)}
        getRowId={(row) => row.id}
        getRowHeight={() => 'auto'}
        pageSizeOptions={GHA_EMAIL_DOMAIN_RULES_PAGE_SIZE_OPTIONS}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        paginationMode="server"
        rowCount={data?.total ?? 0}
        loading={isFetching}
        disableRowSelectionOnClick
        disableColumnMenu
        slots={{ pagination: GridPagination }}
        sx={{ '& .MuiDataGrid-cell': { py: 1, display: 'flex', alignItems: 'center' } }}
        autoHeight
      />

      <BlockedIdentityDialog
        open={dialogOpen}
        initialIdentity={identityToEdit}
        isSaving={upsertMutation.isPending}
        submitError={submitError}
        onClose={closeDialog}
        onSubmit={async (value, input) => {
          setSubmitError(null);
          try {
            await upsertMutation.mutateAsync({ id: identityToEdit?.id, value, input });
            closeDialog();
          } catch (submitException) {
            // Malformed and placeholder addresses come back as 400s with the reason — show verbatim.
            setSubmitError(submitException instanceof Error ? submitException.message : 'Failed to save');
          }
        }}
      />

      <GHABulkOperationDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk upload blocked identities"
        csvHeader="kind,value,reason,expires_at,ip,booking_id"
        csvRows={[
          'email,name@gmail.com,OTA test booking,2027-10-01,49.36.138.227,441431481',
          'email,other@yahoo.com,,,,',
        ]}
        csvNotes={
          <>
            • Required columns <strong>kind,value,reason</strong>; kind is email and reason may be empty
            <br />• Optional, in any order: expires_at (a date), and the evidence columns{' '}
            {GHA_BLOCKED_IDENTITY_EVIDENCE_FIELDS.map(({ column }) => column).join(', ')}
            <br />• Each row creates or overwrites that address. Any bad row rejects the whole file.
          </>
        }
        submitButtonLabel="Upload"
        successMessage="Blocked identities uploaded."
        // The mutation's snackbar reports the created/updated counts, and the list refetches, so
        // the dialog's own "refresh the page" success copy is not needed.
        showSuccessAlert={false}
        showSuccessSnackbar={false}
        onSubmit={async (file) => {
          await bulkMutation.mutateAsync(file);
        }}
      />
    </Box>
  );
}
